"use client";
// "use client" preserved — module exports browser-only SDK code; harmless on server-only consumers.

import { Upload } from "@aws-sdk/lib-storage";
import type { S3Client } from "@aws-sdk/client-s3";
import { AWS_BUCKETS, getAwsRegionConfig } from "@/config/aws-regions";
import {
  getCachedCognitoClient,
  invalidateCognitoClient,
  isCredentialError,
} from "@speakai/common/src/upload/cognito-credentials";

// Server re-derives the real MIME via getMediaMetaDetails for audio/video;
// pinning octet-stream prevents stored-XSS via attacker-controlled Content-Type echoing back from S3/CloudFront.
const FORCED_UPLOAD_CONTENT_TYPE = "application/octet-stream";

export type ErrorCategory =
  | "credential_expired"
  | "aborted"
  | "network"
  | "size_limit"
  | "content_type"
  | "unknown";

export interface S3UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/** Generic upload handle returned by both wrappers. T defaults to S3UploadResult (lowercase shape). */
export interface UploadHandle<T = S3UploadResult> {
  status: "pending";
  promise: Promise<T>;
  abort: () => void;
}

/** Lowercase-keyed result shape — matches use-upload.ts reads at lines 474-484 (bucket, key, location). */
export interface S3UploadResult {
  status: "success" | "error" | "aborted";
  bucket?: string;
  key?: string;
  location?: string;
  /** User-safe error copy; do NOT echo raw SDK error strings into UI. */
  error?: string;
  errorCategory?: ErrorCategory;
}

/**
 * Recording wrapper result — preserves the historical uppercase-keyed shape.
 * upload-record-page.tsx reads result.data.Bucket / result.data.Key / result.data.Location.
 */
export interface RecordingResult {
  status: "success" | "error" | "aborted";
  data?: { Bucket: string; Key: string; Location?: string };
  error?: unknown;
  message?: string;
}

interface PrimitiveOpts {
  body: Blob;
  /** Server-issued bucket id — `AWS_BUCKETS` enum value OR raw string (forwarded to `getAwsRegionConfig`, which falls back when unknown). */
  bucket: AWS_BUCKETS | string;
  key: string;
  maxBytes?: number;
  onProgress?: (p: S3UploadProgress) => void;
}

function createUploadInstance(
  client: S3Client,
  params: { Bucket: string; Key: string; Body: Blob; ContentType: string },
  abortController: AbortController,
  onProgress?: (p: S3UploadProgress) => void,
): Upload {
  const upload = new Upload({
    client,
    params,
    queueSize: 4,
    partSize: Math.max(5 * 1024 * 1024, Math.ceil(params.Body.size / 9000)),
    leavePartsOnError: false,
  });

  if (onProgress) {
    upload.on("httpUploadProgress", (evt) => {
      if (!abortController.signal.aborted) {
        const loaded = evt.loaded ?? 0;
        const total = evt.total ?? params.Body.size;
        const percent = total > 0 ? Math.round((100 * loaded) / total) : 0;
        onProgress({ loaded, total, percent });
      }
    });
  }

  // { once: true } prevents double-abort if the signal fires while done() is in-flight
  abortController.signal.addEventListener("abort", () => { upload.abort(); }, { once: true });

  return upload;
}

function classifyError(err: unknown): ErrorCategory {
  if (isCredentialError(err)) return "credential_expired";
  if (err && typeof err === "object") {
    const e = err as { name?: string; code?: string; message?: string };
    if (e.name === "AbortError" || e.code === "RequestAbortedError") return "aborted";
    if (e.name === "NetworkingError" || e.code === "NetworkingError") return "network";
    // Browsers reject an in-flight fetch with a bare TypeError when the
    // connection drops mid-upload (Chrome: "Failed to fetch", Safari: "Load
    // failed"). The AWS SDK's fetch-based request handler surfaces these
    // unwrapped, so without this check every connectivity blip during a
    // multipart PUT was bucketed as "unknown" instead of "network" — masking
    // a large share of large-file upload failures on mobile (SPEAK-CLIENT-6T).
    if (
      e.name === "TypeError" &&
      typeof e.message === "string" &&
      /failed to fetch|load failed|network ?error/i.test(e.message)
    ) {
      return "network";
    }
    if (typeof e.message === "string" && /content-?type/i.test(e.message)) return "content_type";
  }
  return "unknown";
}

/**
 * Internal primitive — region-resolves, fetches Cognito credentials, performs multipart upload,
 * single-shot refresh-and-retry on credential expiry. Not exported — consumers use the typed wrappers.
 */
function uploadToS3Cognito(opts: PrimitiveOpts): UploadHandle {
  const abortController = new AbortController();
  let currentUpload: Upload | null = null;
  const abort = () => {
    if (currentUpload) currentUpload.abort();
    abortController.abort();
  };

  const promise = (async (): Promise<S3UploadResult> => {
    if (opts.maxBytes && opts.body.size > opts.maxBytes) {
      const limitGb = Math.round(opts.maxBytes / (1024 * 1024 * 1024));
      return { status: "error", error: `File exceeds ${limitGb}GB upload limit`, errorCategory: "size_limit" };
    }

    if (abortController.signal.aborted) {
      return { status: "aborted", error: "Upload was aborted before it started", errorCategory: "aborted" };
    }

    const regionConfig = getAwsRegionConfig(opts.bucket);
    const clientPromise = getCachedCognitoClient(regionConfig);

    const runOnce = async (client: S3Client): Promise<S3UploadResult> => {
      currentUpload = createUploadInstance(
        client,
        {
          Bucket: regionConfig.bucketName,
          Key: opts.key,
          Body: opts.body,
          ContentType: FORCED_UPLOAD_CONTENT_TYPE,
        },
        abortController,
        opts.onProgress,
      );
      const result = await currentUpload.done();
      return {
        status: "success",
        bucket: result.Bucket ?? regionConfig.bucketName,
        key: result.Key ?? opts.key,
        location: result.Location,
      };
    };

    try {
      const client = await clientPromise;
      try {
        return await runOnce(client);
      } catch (uploadError) {
        if (abortController.signal.aborted) {
          return { status: "aborted", error: "Upload was aborted by user", errorCategory: "aborted" };
        }
        if (isCredentialError(uploadError)) {
          try {
            const freshClient = await invalidateCognitoClient(regionConfig, clientPromise);
            return await runOnce(freshClient);
          } catch (retryError) {
            // Caller surfaces errorCategory to analytics; user sees generic copy to avoid leaking AWS internals.
            return {
              status: "error",
              error: "Failed to upload file",
              errorCategory: classifyError(retryError),
            };
          }
        }
        return {
          status: "error",
          error: "Failed to upload file",
          errorCategory: classifyError(uploadError),
        };
      }
    } catch (prepError) {
      return {
        status: "error",
        error: "Upload preparation failed",
        errorCategory: classifyError(prepError),
      };
    } finally {
      currentUpload = null;
    }
  })();

  return { status: "pending", promise, abort };
}

/**
 * Sanitize a filename for use as an S3 key path segment.
 * Strips path separators (no traversal into other prefixes), control chars (defense-in-depth
 * against header injection), replaces non-alphanumerics with underscores, caps at 200 chars.
 * Single canonical sanitizer for all Cognito uploads.
 */
export function sanitizeUploadFileName(name: string, extension: string): string {
  const stripped = name
    .replace(/[/\\]/g, "_")
    .replace(/[\x00-\x1f]/g, "_")
    .replace(/[^A-Za-z0-9]+/g, " ")
    .replace(/\s+/g, "_")
    .slice(0, 200)
    .replace(/^_+|_+$/g, "")
    .trim();
  const safe = stripped.length > 0 ? stripped : `${Date.now()}`;
  return `${safe}.${extension}`;
}

// ---------- Public wrappers ----------

export interface UploadRecordingOptions {
  userId: string;
  filename: string;
  /** "audio" for mic recordings; "video" for camera or screen recordings. */
  mediaType: "audio" | "video";
  /** From UserSubscription.bucketId — server-issued region routing. Forwarded raw. */
  bucketId?: AWS_BUCKETS | string;
  maxBytes?: number;
  onProgress?: (event: S3UploadProgress) => void;
}

/**
 * Recorder contract — preserves the historical uppercase-keyed result shape for
 * backward compatibility with upload-record-page.tsx reads (result.data.Bucket / Key / Location).
 */
export function uploadRecordingToS3(
  blob: Blob,
  opts: UploadRecordingOptions,
): {
  status: "pending";
  promise: Promise<RecordingResult>;
  abort: () => void;
} {
  const dotIdx = opts.filename.lastIndexOf(".");
  const extension = dotIdx > 0 ? opts.filename.slice(dotIdx + 1) : "webm";
  const baseName = dotIdx > 0 ? opts.filename.slice(0, dotIdx) : opts.filename;
  const key = `media/${opts.userId}/${opts.mediaType}/${Date.now()}-${sanitizeUploadFileName(baseName, extension)}`;

  const inner = uploadToS3Cognito({
    body: blob,
    bucket: opts.bucketId ?? AWS_BUCKETS.SPEAKAI_STORAGE_US_EAST_1,
    key,
    maxBytes: opts.maxBytes,
    onProgress: opts.onProgress,
  });

  return {
    status: "pending",
    abort: inner.abort,
    promise: inner.promise.then<RecordingResult>((r) => {
      if (r.status === "success") {
        return {
          status: "success",
          data: { Bucket: r.bucket!, Key: r.key!, Location: r.location },
        };
      }
      // The recorder page reads r.message (string) and r.error (unknown) — preserve those shapes.
      return r.errorCategory === "size_limit"
        ? { status: r.status, message: r.error ?? "Recording exceeds upload limit", error: r.error }
        : { status: r.status, message: r.error, error: r.error };
    }),
  };
}

export interface UploadFileOptions {
  userId: string;
  filename: string;
  /**
   * Discriminated union — narrower than UploadFileItem.mediaType: string, preventing
   * typos from silently reaching the S3 key path.
   */
  mediaType: "audio" | "video" | "text" | "url";
  /** From UserSubscription.bucketId — server-issued region routing. Forwarded raw. */
  bucketId?: AWS_BUCKETS | string;
  maxBytes?: number;
  onProgress?: (event: S3UploadProgress) => void;
}

/**
 * File-upload contract — lowercase-keyed { bucket, key, location } so use-upload.ts
 * can stamp file.s3 unchanged (reads at lines 474-484).
 */
export function uploadFileToS3(file: File | Blob, opts: UploadFileOptions): UploadHandle {
  const dotIdx = opts.filename.lastIndexOf(".");
  const extension = dotIdx > 0 ? opts.filename.slice(dotIdx + 1) : "bin";
  const baseName = dotIdx > 0 ? opts.filename.slice(0, dotIdx) : opts.filename;
  const key = `media/${opts.userId}/${opts.mediaType}/${Date.now()}-${sanitizeUploadFileName(baseName, extension)}`;

  return uploadToS3Cognito({
    body: file,
    bucket: opts.bucketId ?? AWS_BUCKETS.SPEAKAI_STORAGE_US_EAST_1,
    key,
    maxBytes: opts.maxBytes,
    onProgress: opts.onProgress,
  });
}

// Back-compat type aliases — the recorder's public type was UploadRecordingResult + UploadRecordingHandle.
export type { RecordingResult as UploadRecordingResult };
export type UploadRecordingHandle = ReturnType<typeof uploadRecordingToS3>;
